import { AgxSpacer, useWindowSize, Breakpoints } from '@urbanx/agx.ui-components2';
import { Tabs } from 'types/Tabs';
import AgencyAgreementInfoPanel from './AgencyAgreement/AgencyAgreementInfoPanel';
import AgencyAgreementSubmissionInfoPanel from './AgencyAgreementSubmission/AgencyAgreementSubmissionInfoPanel';
import ListingsPanel from './Listing/ListingsPanel';
import './CampaignInfoPanel.scss';
import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { CampaignDetailModel, CampaignDetailModelStage } from '@urbanx/agx.campaigns.client';
import FormDetailsHeader from './Common/FormDetailsHeader';
import type { MarketingPackageProjection } from '@urbanx/agx.marketing.client';
import { useAppSelector } from 'hooks/useAppSelector';

interface CampaignInfoPanelProps {
  campaign?: CampaignDetailModel;
  currentTab: Tabs;
  mobile?: boolean;
}

const CampaignInfoPanel = ({ campaign, currentTab, mobile = false }: CampaignInfoPanelProps) => {
  const [visible, setVisible] = useState(false);
  const [currentCampaignId, setCurrentCampaignId] = useState<string | undefined>(
    campaign?.campaignId
  );
  const [selectedMarketingPackage, setSelectedMarketingPackage] = useState<
    MarketingPackageProjection | undefined
  >();
  const selectedContractId = useAppSelector((state) => state.campaigns.selectedContractId);

  const fadeIn = () => {
    setCurrentCampaignId(campaign?.campaignId);
    setVisible(false);

    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  };

  useEffect(() => {
    return fadeIn();
  }, []);

  useEffect(() => {
    if (currentCampaignId === campaign?.campaignId) return;

    setSelectedMarketingPackage(undefined);
    return fadeIn();
  }, [campaign]);

  useEffect(() => {
    if (!selectedContractId) return;

    setSelectedMarketingPackage(undefined);
  }, [selectedContractId]);

  const { stage } = campaign ?? {};

  const screenSize = useWindowSize();

  return (
    <AgxSpacer
      direction="vertical"
      className={clsx('campaignInfoPanel', visible && 'visible')}
      padding="lg"
      gap="md"
      contentJustification="start"
      contentAlignment="center"
    >
      {campaign && (
        <FormDetailsHeader
          id={'form-details-header'}
          campaign={campaign}
          currentTab={currentTab}
          selectedMarketingPackage={selectedMarketingPackage}
          onMarketingPackageBack={() => setSelectedMarketingPackage(undefined)}
        />
      )}
      {campaign && (
        <AgxSpacer
          className="listingPanelContent"
          direction="vertical"
          gap="md"
          fill
          contentAlignment="center"
          padding={
            screenSize === Breakpoints.Desktop
              ? {
                  left: 'spacing-7xl',
                  right: 'spacing-7xl',
                }
              : screenSize === Breakpoints.Tablet_Landscape
                ? {
                    left: 'spacing-4xl',
                    right: 'spacing-4xl',
                  }
                : undefined
          }
        >
          {(stage === CampaignDetailModelStage.AgreementDraft ||
            stage === CampaignDetailModelStage.AgreementAgentSigning ||
            stage === CampaignDetailModelStage.AgreementVendorSigning ||
            stage === CampaignDetailModelStage.AgreementSignedByAllParties) && (
            <AgencyAgreementInfoPanel currentTab={currentTab} campaign={campaign} mobile={mobile} />
          )}
          {stage === CampaignDetailModelStage.AgreementSubmissionIncomplete && (
            <AgencyAgreementSubmissionInfoPanel
              campaign={campaign}
              mobile={mobile}
              currentTab={currentTab}
            />
          )}
          {(stage === CampaignDetailModelStage.AgreementPendingCompliance ||
            stage === CampaignDetailModelStage.ListingCurrent ||
            stage === CampaignDetailModelStage.ListingUnderContract ||
            stage === CampaignDetailModelStage.ListingUnconditional ||
            stage === CampaignDetailModelStage.ListingWithdrawn ||
            stage === CampaignDetailModelStage.ListingSold) && (
            <ListingsPanel
              campaign={campaign}
              mobile={mobile}
              currentTab={currentTab}
              selectedMarketingPackage={selectedMarketingPackage}
              setSelectedMarketingPackage={setSelectedMarketingPackage}
            />
          )}
        </AgxSpacer>
      )}
    </AgxSpacer>
  );
};

export default CampaignInfoPanel;
